import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Stack,
  InputAdornment,
  Avatar,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/AuthProvider';

// 실제 데이터베이스 스키마에 맞는 인터페이스
interface Product {
  product_id: number;
  product_name: string;
  price: number;
  status: string;
  main_images: string[];
  detail_images: string[];
  store_name: string;
  description: string;
  keywords: string[];
  created_at: string;
  updated_at: string;
  seller_id: string;
}

interface ProductOption {
  id: number;
  product_id: number;
  color_name: string;
  size: string;
  quantity: number;
  color_hex: string;
  created_at: string;
  updated_at: string;
}

// TailAdmin 스타일
const tailAdminStyles = {
  background: {
    light: '#f8fafc',
    dark: '#111827'
  },
  card: {
    light: '#ffffff',
    dark: '#1f2937',
    border: '#e5e7eb',
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
  },
  text: {
    primary: '#1f2937',
    secondary: '#6b7280',
    accent: '#3b82f6'
  },
  colors: {
    primary: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444'
  }
};

const statusLabel = (status: string, t: (key: string) => string, onClick?: () => void) => {
  const chipProps = {
    label: status === 'active' ? t('products.active') : status === 'inactive' ? t('products.inactive') : t('products.pending'),
    color: status === 'active' ? 'success' : status === 'inactive' ? 'default' : 'warning' as any,
    size: 'small' as const,
    sx: {
      fontWeight: 700,
      borderRadius: 1,
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { opacity: 0.8 } : {}
    },
    onClick: onClick
  };

  return <Chip {...chipProps} />;
};

// 합산 재고 계산 함수
const calculateTotalStock = (options: ProductOption[]): number => {
  return options.reduce((total, option) => total + option.quantity, 0);
};

// 확장 가능한 행 컴포넌트
function ExpandableRow({ product, options, onEdit, onDelete, onStatusChange, isEditing, editingProductId, t }: {
  product: Product;
  options: ProductOption[];
  onEdit: (product: Product) => void;
  onDelete: (productId: number) => void;
  onStatusChange: (productId: number, newStatus: string) => void;
  isEditing: boolean;
  editingProductId: number | null;
  t: (key: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const handleStatusClick = () => {
    const newStatus = product.status === 'active' ? 'inactive' : 'active';
    onStatusChange(product.product_id, newStatus);
  };

  return (
    <>
      <TableRow sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
        <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
          <IconButton
            size="small"
            onClick={handleExpand}
            sx={{ color: tailAdminStyles.text.primary }}
          >
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={Array.isArray(product.main_images) ? product.main_images[0] : product.main_images}
              alt={product.product_name}
              sx={{ width: 50, height: 50 }}
            />
            <Box>
              <Typography variant="body2" fontWeight={600} color={tailAdminStyles.text.primary}>
                {product.product_name}
              </Typography>
              <Typography variant="caption" color={tailAdminStyles.text.secondary}>
                {product.store_name}
              </Typography>
              <Typography variant="caption" color={tailAdminStyles.text.secondary} sx={{ display: 'block' }}>
                Seller: {product.seller_id}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
          <Typography color={tailAdminStyles.text.primary}>
            {product.price.toLocaleString()}원
          </Typography>
        </TableCell>
        <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
          {options.length > 0 ? (
            <Typography variant="body2" fontWeight={600} color={tailAdminStyles.text.primary}>
              {calculateTotalStock(options).toLocaleString()}
            </Typography>
          ) : (
            <Typography variant="body2" color={tailAdminStyles.text.secondary}>
              {t('products.noOption')}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
          {statusLabel(product.status, t, handleStatusClick)}
        </TableCell>
        <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
          <IconButton
            size="small"
            onClick={() => onEdit(product)}
            disabled={isEditing && editingProductId === product.product_id}
            sx={{ color: tailAdminStyles.text.primary }}
          >
            {isEditing && editingProductId === product.product_id ? (
              <CircularProgress size={16} />
            ) : (
              <Edit />
            )}
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(product.product_id)} sx={{ color: tailAdminStyles.text.primary }}>
            <Delete />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: `1px solid ${tailAdminStyles.card.border}` }} colSpan={6}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div" color={tailAdminStyles.text.primary}>
                {t('products.productOptions')}
              </Typography>
              <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
                      <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>{t('products.color')}</TableCell>
                      <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>{t('products.size')}</TableCell>
                      <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>{t('common.quantity')}</TableCell>
                      <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {options.map((option) => (
                      <TableRow key={option.id}>
                        <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: option.color_hex || '#ccc',
                                border: `1px solid ${tailAdminStyles.card.border}`
                              }}
                            />
                            <Typography color={tailAdminStyles.text.primary}>{option.color_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                          <Typography color={tailAdminStyles.text.primary}>{option.size}</Typography>
                        </TableCell>
                        <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                          <Typography color={tailAdminStyles.text.primary}>{option.quantity}</Typography>
                        </TableCell>
                        <TableCell sx={{ color: tailAdminStyles.text.primary, borderBottom: `1px solid ${tailAdminStyles.card.border}` }}>
                          <IconButton size="small" sx={{ color: tailAdminStyles.text.primary }}>
                            <Edit />
                          </IconButton>
                          <IconButton size="small" sx={{ color: tailAdminStyles.text.primary }}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function AdminProducts() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [productOptions, setProductOptions] = useState<{ [key: number]: ProductOption[] }>({});
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // 삭제 확인 팝업 상태 추가
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<number | null>(null);

  // 페이지네이션 상태 추가
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const productsPerPage = 10;

  // 관리자용 모든 상품 데이터 로드 (seller 필터링 제거)
  useEffect(() => {
    const fetchProducts = async () => {
      if (!user) {
        console.error('❌ 사용자 정보를 가져올 수 없습니다.');
        return;
      }

      try {
        setLoading(true);
        console.log('=== 관리자 권한으로 모든 상품 데이터 로드 시작 ===');
        console.log('✅ 관리자 사용자 정보:', user.id);

        // 1. 전체 상품 개수 조회 (seller 필터링 제거)
        const { count: totalCount, error: countError } = await supabase
          .from('products')
          .select('*', { count: 'exact', head: true });

        if (countError) {
          console.error('❌ 전체 상품 개수 조회 실패:', countError);
          return;
        }

        setTotalProducts(totalCount || 0);
        const totalPagesCount = Math.ceil((totalCount || 0) / productsPerPage);
        setTotalPages(totalPagesCount);
        console.log('📊 전체 상품 개수:', totalCount, '총 페이지:', totalPagesCount);

        // 2. 페이지네이션된 모든 상품 데이터 조회
        const from = (currentPage - 1) * productsPerPage;
        const to = from + productsPerPage - 1;

        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('*')
          .range(from, to)
          .order('created_at', { ascending: false });

        if (productsError) {
          console.error('❌ 상품 데이터 로드 실패:', productsError);
          return;
        }

        console.log('📦 조회된 상품 개수:', productsData?.length || 0);
        console.log('현재 페이지:', currentPage, '범위:', from, '-', to);

        if (productsData && productsData.length > 0) {
          setProducts(productsData);

          // 3. 모든 상품의 옵션 데이터를 한번에 조회
          console.log('🔍 모든 상품의 옵션 데이터 조회 시작...');
          const productIds = productsData.map(p => p.product_id);

          const { data: allOptionsData, error: optionsError } = await supabase
            .from('product_options')
            .select('*')
            .in('product_id', productIds);

          if (optionsError) {
            console.error('❌ 옵션 데이터 조회 실패:', optionsError);
            setProductOptions({});
          } else {
            // 옵션 데이터를 product_id별로 그룹화
            const optionsByProduct: { [key: number]: ProductOption[] } = {};
            allOptionsData?.forEach(option => {
              if (!optionsByProduct[option.product_id]) {
                optionsByProduct[option.product_id] = [];
              }
              optionsByProduct[option.product_id].push(option);
            });

            setProductOptions(optionsByProduct);
            console.log('✅ 옵션 데이터 로드 완료:', Object.keys(optionsByProduct).length, '개 상품의 옵션');
          }
        } else {
          console.log('⚠️ 등록된 상품이 없습니다.');
          setProducts([]);
          setProductOptions({});
        }

        console.log('✅ 모든 상품 데이터 로드 완료');
      } catch (error) {
        console.error('❌ 데이터 로드 중 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentPage, user]);

  const handleEditProduct = async (product: Product) => {
    console.log('=== 수정 버튼 클릭 ===');
    console.log('선택된 상품:', product);

    // 이미 수정 중이면 중복 실행 방지
    if (isEditing) {
      console.log('⚠️ 이미 수정 중입니다.');
      return;
    }

    setIsEditing(true);
    setEditingProductId(product.product_id);

    try {
      // 해당 상품의 옵션 데이터를 먼저 로드
      console.log('🔍 수정을 위한 옵션 데이터 로드 중...');
      const { data: optionsData, error: optionsError } = await supabase
        .from('product_options')
        .select('*')
        .eq('product_id', product.product_id);

      if (optionsError) {
        console.error('❌ 옵션 데이터 로드 실패:', optionsError);
        alert('옵션 데이터를 불러오는데 실패했습니다.');
        return;
      }

      console.log('✅ 옵션 데이터 로드 완료:', optionsData);

      // 상품 편집 다이얼로그 열기
      setEditingProduct(product);
      setOpenDialog(true);

    } catch (error) {
      console.error('❌ 수정 준비 중 오류:', error);
      alert('수정 준비 중 오류가 발생했습니다.');
    } finally {
      setIsEditing(false);
      setEditingProductId(null);
    }
  };

  // 삭제 확인 함수
  const handleDeleteConfirm = (productId: number) => {
    setProductToDelete(productId);
    setDeleteDialogOpen(true);
  };

  // 실제 삭제 실행 함수
  const handleDeleteProduct = async (productId: number) => {
    try {
      // 삭제할 상품 정보 가져오기 (이미지 URL을 위해)
      const productToDelete = products.find(p => p.product_id === productId);

      // 1. 먼저 관련된 옵션들 삭제
      const { error: optionsError } = await supabase
        .from('product_options')
        .delete()
        .eq('product_id', productId);

      if (optionsError) {
        console.error('옵션 삭제 실패:', optionsError);
        return;
      }

      // 2. 상품 삭제
      const { error: productError } = await supabase
        .from('products')
        .delete()
        .eq('product_id', productId);

      if (productError) {
        console.error('상품 삭제 실패:', productError);
        return;
      }

      // 3. 로컬 상태 업데이트
      setProducts(products.filter(product => product.product_id !== productId));
      const newOptions = { ...productOptions };
      delete newOptions[productId];
      setProductOptions(newOptions);

      // 팝업 닫기
      setDeleteDialogOpen(false);
      setProductToDelete(null);

      console.log(`✅ 상품 ${productId} 완전 삭제 완료`);

    } catch (error) {
      console.error('삭제 중 오류:', error);
    }
  };

  const handleSaveProduct = async (productData: Partial<Product>) => {
    try {
      if (editingProduct) {
        // 기존 상품 업데이트
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('product_id', editingProduct.product_id);

        if (error) {
          console.error('상품 업데이트 실패:', error);
          return;
        }

        // 로컬 상태 업데이트
        setProducts(products.map(p =>
          p.product_id === editingProduct.product_id ? { ...p, ...productData } : p
        ));
      }
      setOpenDialog(false);
      setEditingProduct(null);
    } catch (error) {
      console.error('저장 중 오류:', error);
    }
  };

  // 상태 변경 핸들러
  const handleStatusChange = async (productId: number, newStatus: string) => {
    try {
      console.log(`🔄 상품 상태 변경: ${productId} -> ${newStatus}`);

      // 데이터베이스 업데이트
      const { error } = await supabase
        .from('products')
        .update({ status: newStatus })
        .eq('product_id', productId);

      if (error) {
        console.error('상태 변경 실패:', error);
        alert('상태 변경에 실패했습니다.');
        return;
      }

      // 로컬 상태 업데이트
      setProducts(products.map(p =>
        p.product_id === productId ? { ...p, status: newStatus } : p
      ));

      console.log(`✅ 상품 상태 변경 완료: ${productId} -> ${newStatus}`);
    } catch (error) {
      console.error('상태 변경 중 오류:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  // 페이지네이션 함수들
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.store_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.seller_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      tab === 0 ||
      (tab === 1 && product.status === 'active') ||
      (tab === 2 && product.status === 'inactive') ||
      (tab === 3 && product.status === 'pending');
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        backgroundColor: tailAdminStyles.background.light
      }}>
        <CircularProgress sx={{ color: tailAdminStyles.colors.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: tailAdminStyles.background.light,
      p: 3
    }}>
      <Typography
        variant="h4"
        fontWeight={700}
        mb={3}
        sx={{ color: tailAdminStyles.text.primary }}
      >
        {t('products.management')}
      </Typography>

      <Paper sx={{
        backgroundColor: tailAdminStyles.card.light,
        borderRadius: 2,
        boxShadow: tailAdminStyles.card.shadow,
        border: `1px solid ${tailAdminStyles.card.border}`,
        p: 3,
        mb: 3
      }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            placeholder={t('products.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: tailAdminStyles.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: tailAdminStyles.card.light,
                '& fieldset': {
                  borderColor: tailAdminStyles.card.border
                },
                '&:hover fieldset': {
                  borderColor: tailAdminStyles.colors.primary
                }
              }
            }}
          />

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                color: tailAdminStyles.text.secondary,
                '&.Mui-selected': {
                  color: tailAdminStyles.colors.primary
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: tailAdminStyles.colors.primary
              }
            }}
          >
            <Tab label={t('products.all')} sx={{ fontWeight: 700, minHeight: 40 }} />
            <Tab label={t('products.active')} sx={{ fontWeight: 700, minHeight: 40 }} />
            <Tab label={t('products.inactive')} sx={{ fontWeight: 700, minHeight: 40 }} />
            <Tab label={t('products.pending')} sx={{ fontWeight: 700, minHeight: 40 }} />
          </Tabs>
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{
        backgroundColor: tailAdminStyles.card.light,
        borderRadius: 2,
        boxShadow: tailAdminStyles.card.shadow,
        border: `1px solid ${tailAdminStyles.card.border}`
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8fafc' }}>
              <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}></TableCell>
              <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>{t('products.productName')}</TableCell>
              <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>{t('common.price')}</TableCell>
              <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>{t('products.totalStock')}</TableCell>
              <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>{t('common.status')}</TableCell>
              <TableCell sx={{ color: tailAdminStyles.text.primary, fontWeight: 600 }}>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product) => (
              <ExpandableRow
                key={product.product_id}
                product={product}
                options={productOptions[product.product_id] || []}
                onEdit={handleEditProduct}
                onDelete={handleDeleteConfirm}
                onStatusChange={handleStatusChange}
                isEditing={isEditing}
                editingProductId={editingProductId}
                t={t}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            startIcon={<KeyboardArrowLeft />}
            sx={{
              borderColor: tailAdminStyles.card.border,
              color: tailAdminStyles.text.primary,
              '&:hover': {
                borderColor: tailAdminStyles.colors.primary,
                bgcolor: '#f3f4f6'
              }
            }}
          >
            {t('common.previous')}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = index + 1;
              // 현재 페이지 주변의 5개 페이지만 표시
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
              ) {
                return (
                  <Button
                    key={pageNumber}
                    variant={currentPage === pageNumber ? "contained" : "outlined"}
                    onClick={() => handlePageChange(pageNumber)}
                    sx={{
                      minWidth: 40,
                      ...(currentPage === pageNumber ? {
                        backgroundColor: tailAdminStyles.colors.primary,
                        '&:hover': {
                          backgroundColor: '#2563eb'
                        }
                      } : {
                        borderColor: tailAdminStyles.card.border,
                        color: tailAdminStyles.text.primary,
                        '&:hover': {
                          borderColor: tailAdminStyles.colors.primary,
                          bgcolor: '#f3f4f6'
                        }
                      })
                    }}
                  >
                    {pageNumber}
                  </Button>
                );
              } else if (
                pageNumber === currentPage - 3 ||
                pageNumber === currentPage + 3
              ) {
                return <Typography key={pageNumber} color={tailAdminStyles.text.secondary}>...</Typography>;
              }
              return null;
            })}
          </Box>

          <Button
            variant="outlined"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            endIcon={<KeyboardArrowRight />}
            sx={{
              borderColor: tailAdminStyles.card.border,
              color: tailAdminStyles.text.primary,
              '&:hover': {
                borderColor: tailAdminStyles.colors.primary,
                bgcolor: '#f3f4f6'
              }
            }}
          >
            {t('common.next')}
          </Button>

          <Typography variant="body2" color={tailAdminStyles.text.secondary} sx={{ ml: 2 }}>
            {t('products.totalProductsCount', { count: totalProducts })}
          </Typography>
        </Box>
      )}

      {/* 상품 편집 다이얼로그 */}
      <ProductDialog
        open={openDialog}
        product={editingProduct}
        onClose={() => setOpenDialog(false)}
        onSave={handleSaveProduct}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => productToDelete && handleDeleteProduct(productToDelete)}
      />
    </Box>
  );
}

interface ProductDialogProps {
  open: boolean;
  product: Product | null;
  onClose: () => void;
  onSave: (productData: Partial<Product>) => void;
}

function ProductDialog({ open, product, onClose, onSave }: ProductDialogProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    product_name: product?.product_name || '',
    store_name: product?.store_name || '',
    price: product?.price || 0,
    status: product?.status || 'pending',
    description: product?.description || '',
  });

  useEffect(() => {
    if (product) {
      setFormData({
        product_name: product.product_name,
        store_name: product.store_name,
        price: product.price,
        status: product.status,
        description: product.description,
      });
    }
  }, [product]);

  const handleSubmit = () => {
    onSave(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: tailAdminStyles.card.light,
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ color: tailAdminStyles.text.primary }}>
        {product ? t('products.editDialogTitle') : t('products.newProductTitle')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('products.productName')}
              value={formData.product_name}
              onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: tailAdminStyles.card.border
                  },
                  '&:hover fieldset': {
                    borderColor: tailAdminStyles.colors.primary
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('products.storeName')}
              value={formData.store_name}
              onChange={(e) => setFormData({ ...formData, store_name: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: tailAdminStyles.card.border
                  },
                  '&:hover fieldset': {
                    borderColor: tailAdminStyles.colors.primary
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('common.description')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              minRows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: tailAdminStyles.card.border
                  },
                  '&:hover fieldset': {
                    borderColor: tailAdminStyles.colors.primary
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('common.price')}
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: tailAdminStyles.card.border
                  },
                  '&:hover fieldset': {
                    borderColor: tailAdminStyles.colors.primary
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: tailAdminStyles.card.border
                },
                '&:hover fieldset': {
                  borderColor: tailAdminStyles.colors.primary
                }
              }
            }}>
              <InputLabel>{t('common.status')}</InputLabel>
              <Select
                value={formData.status}
                label={t('common.status')}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="active">{t('products.active')}</MenuItem>
                <MenuItem value="inactive">{t('products.inactive')}</MenuItem>
                <MenuItem value="pending">{t('products.pending')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: tailAdminStyles.text.secondary }}>{t('common.cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            backgroundColor: tailAdminStyles.colors.primary,
            '&:hover': {
              backgroundColor: '#2563eb'
            }
          }}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// 삭제 확인 팝업 컴포넌트
function DeleteConfirmDialog({ open, onClose, onConfirm }: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: tailAdminStyles.card.light,
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, color: tailAdminStyles.text.primary }}>
        {t('products.deleteTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ textAlign: 'center', mb: 2, color: tailAdminStyles.text.primary }}>
          {t('products.deleteConfirm')}
        </Typography>
        <Typography sx={{ textAlign: 'center', color: tailAdminStyles.text.secondary }}>
          {t('products.deleteWarning')}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{
          minWidth: 100,
          borderColor: tailAdminStyles.card.border,
          color: tailAdminStyles.text.primary,
          '&:hover': {
            borderColor: tailAdminStyles.colors.primary,
            bgcolor: '#f3f4f6'
          }
        }}>
          {t('common.cancel')}
        </Button>
        <Button onClick={onConfirm} variant="contained" sx={{
          minWidth: 100,
          bgcolor: tailAdminStyles.colors.error,
          color: '#fff',
          '&:hover': {
            bgcolor: '#dc2626'
          }
        }}>
          {t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
